<template>
  <van-popup
    :value="value"
    position="bottom"
    round
    safe-area-inset-bottom
    class="publish-category-picker"
    @input="$emit('input', $event)"
  >
    <div class="publish-category-picker__toolbar">
      <div class="publish-category-picker__title">{{ title }}</div>
      <!-- 关闭{title} -->
      <button
        type="button"
        class="publish-category-picker__close"
        :aria-label="$t('sharedUi.closeDetails', { title: title })"
        @click="handleCancel"
      >
        <van-icon name="cross" />
      </button>
    </div>

    <van-search
      :value="searchValue"
      :placeholder="searchPlaceholder"
      clearable
      class="publish-category-picker__search"
      @input="handleSearch"
    />

    <van-picker
      ref="picker"
      class="publish-category-picker__picker"
      :style="pickerStyle"
      :show-toolbar="false"
      :visible-item-count="pickerVisibleItemCount"
      :item-height="itemHeight"
      :value-key="valueKey"
      :columns="pickerColumns"
      @confirm="handleConfirm"
      @change="handleChange"
    >
      <template v-if="labelFormatter" #option="option">{{ labelFormatter(option) }}</template>
    </van-picker>

    <div class="publish-category-picker__actions">
      <PublishButton block tone="primary" @click="confirm">
        {{ confirmText }}
      </PublishButton>
    </div>
  </van-popup>
</template>

<script>
import PublishButton from "./PublishButton.vue";

export default {
  name: "PublishCategoryPicker",
  components: { PublishButton },
  data() {
    return {
      parentIndex: 0,
    };
  },
  props: {
    // 只格式化选择项显示；确认事件仍返回原始分类名称和索引。
    labelFormatter: { type: Function, default: null },
    value: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    searchValue: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "选择产品分类",
    },
    searchPlaceholder: {
      type: String,
      default: "请输入分类名称搜索",
    },
    confirmText: {
      type: String,
      default: "确认",
    },
    valueKey: {
      type: String,
      default: "name",
    },
    visibleItemCount: {
      type: Number,
      default: 5,
    },
    selectedRow: {
      type: Number,
      default: 2,
    },
    itemHeight: {
      type: Number,
      default: 44,
    },
  },
  computed: {
    isCascade() {
      return this.columns.some(
        (column) => column && Array.isArray(column.children)
      );
    },
    pickerColumns() {
      if (!this.isCascade) return this.columns;
      return [
        { values: this.columns },
        { values: this.getChildren(this.parentIndex) },
      ];
    },
    displayItemCount() {
      return Math.max(1, Math.floor(Number(this.visibleItemCount) || 5));
    },
    displaySelectedRow() {
      return Math.min(
        this.displayItemCount,
        Math.max(1, Math.floor(Number(this.selectedRow) || 2))
      );
    },
    pickerVisibleItemCount() {
      return Math.max(
        this.displayItemCount,
        this.displayItemCount * 2 - this.displaySelectedRow * 2 + 1
      );
    },
    pickerOffsetRows() {
      return (this.pickerVisibleItemCount + 1) / 2 - this.displaySelectedRow;
    },
    pickerStyle() {
      return {
        "--publish-category-picker-height": `${
          this.displayItemCount * this.itemHeight
        }px`,
        "--publish-category-picker-offset": `${
          -this.pickerOffsetRows * this.itemHeight
        }px`,
      };
    },
  },
  watch: {
    columns: {
      immediate: true,
      handler() {
        this.parentIndex = 0;
        this.$nextTick(this.resetCascadeColumns);
      },
    },
  },
  methods: {
    getChildren(parentIndex) {
      const parent = this.columns[parentIndex];
      if (parent && Array.isArray(parent.children) && parent.children.length) {
        return parent.children;
      }
      return [{ [this.valueKey]: "" }];
    },
    normalizeValues(values) {
      if (!Array.isArray(values)) return values;
      return values.map((value) =>
        value && typeof value === "object" ? value[this.valueKey] : value
      );
    },
    resetCascadeColumns() {
      const picker = this.$refs.picker;
      if (!picker || !this.isCascade) return;
      picker.setColumnIndex(0, 0);
      picker.setColumnValues(1, this.getChildren(0));
      picker.setColumnIndex(1, 0);
    },
    handleSearch(value) {
      this.$emit("update:searchValue", value);
      this.$emit("search", value);
    },
    handleConfirm(value, index) {
      this.$emit("confirm", this.normalizeValues(value), index);
    },
    handleChange(picker, values, columnIndex) {
      if (!this.isCascade) {
        this.$emit("change", picker, values, columnIndex);
        return;
      }

      if (columnIndex === 0) {
        this.parentIndex = picker.getColumnIndex(0);
        picker.setColumnValues(1, this.getChildren(this.parentIndex));
        picker.setColumnIndex(1, 0);
      }

      this.$nextTick(() => {
        this.$emit(
          "change",
          picker,
          this.normalizeValues(picker.getValues()),
          picker.getIndexes()
        );
      });
    },
    handleCancel() {
      this.$emit("input", false);
      this.$emit("cancel");
    },
    confirm() {
      if (this.$refs.picker) {
        this.$refs.picker.confirm();
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "./tokens.less";

.publish-category-picker {
  display: flex;
  max-height: calc(100vh - 16px);
  flex-direction: column;
  overflow: hidden;
  background: @publish-surface;
}

.publish-category-picker__toolbar {
  position: relative;
  display: flex;
  height: 46px;
  flex: 0 0 46px;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid @publish-border;
}

.publish-category-picker__title {
  color: @publish-text-primary;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.publish-category-picker__close {
  position: absolute;
  top: 8px;
  right: 12px;
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: @publish-text-secondary;
  font-size: 18px;
  background: transparent;
  border: 0;
  border-radius: 50%;
}

.publish-category-picker__close:active {
  background: @publish-page-bg;
}

.publish-category-picker__search {
  flex: 0 0 auto;
  margin: 0;
  padding: 6px 14px 0;
  background: @publish-surface;

  /deep/ .van-search__content {
    height: 40px;
    min-height: 40px;
    box-sizing: border-box;
    align-items: center;
    padding-left: 10px;
    background: @publish-surface;
    border: 1px solid @publish-border;
    border-radius: 7px;
  }

  /deep/ .van-field__control {
    color: @publish-text-primary;
    font-size: 14px;
  }

  /deep/ .van-field__control::placeholder {
    color: @publish-placeholder;
  }
}

.publish-category-picker__picker {
  height: var(--publish-category-picker-height);
  min-height: var(--publish-category-picker-height);
  flex: 0 0 auto;
  overflow: hidden;

  /deep/ .van-picker__columns {
    margin-top: 0;
    transform: translateY(var(--publish-category-picker-offset));
  }

  /deep/ .van-picker-column {
    min-width: 0;
  }

  /deep/ .van-picker-column__item {
    padding: 0 10px;
    color: @publish-text-secondary;
    font-size: 14px;
  }

  /deep/ .van-picker-column__item--selected {
    color: @publish-text-primary;
    font-size: 15px;
    font-weight: 500;
  }

  /deep/ .van-picker-column__item--disabled {
    color: @publish-placeholder;
  }

  /deep/ .van-picker-column__item .van-ellipsis {
    max-width: 100%;
    white-space: normal;
    word-break: break-word;
  }
}

.publish-category-picker__actions {
  flex: 0 0 auto;
  padding: 6px 14px;
  border-top: 1px solid @publish-border;
}
</style>
